import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useClerk } from "@clerk/clerk-react";
import { apiRequest } from "@/lib/queryClient";

/**
 * Use Clerk's actor-token URL to end the existing admin sign-in before
 * consuming the ticket. A direct signIn.create fails in single-session mode
 * with "You're already signed in." The redirect also reloads all member data.
 */
export function useLoginAsUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await apiRequest("POST", `/api/admin/impersonate/${targetUserId}`);
      const data: { url: string } = await res.json();
      if (!data.url) {
        throw new Error("Couldn't start member sign-in. Please try again.");
      }
      return data.url;
    },
    onSuccess: (url) => {
      queryClient.clear();
      window.location.href = url;
    },
  });
}

/**
 * Ends the active impersonation session: tells the backend to close the audit
 * record, then signs out the member and returns to admin sign-in. The actor
 * handoff ended the original admin session, so it cannot be restored.
 */
export function useReturnToMyAccount() {
  const clerk = useClerk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await apiRequest("POST", "/api/admin/impersonate/end");
      } finally {
        // The actor-token handoff signs out the old admin session. Returning
        // requires authentication again; never reactivate a cached session ID.
        sessionStorage.removeItem("impersonation:adminReturnSessionId");
        queryClient.clear();
        await clerk.signOut({ redirectUrl: "/login" });
      }
    },
  });
}
